import React, { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { normalizeImageUrl } from "@/lib/imageUrl";

/**
 * Photo field: upload is the primary workflow (always visible), with an optional
 * "paste image URL" alternative. Shows preview + Replace + Remove. Dropbox share
 * links are auto-converted to direct links, and broken images show a helpful
 * message instead of a broken thumbnail.
 */
export default function ImageUploadField({ value, onChange, label = "Photo", testid = "image-upload", aspect = "aspect-square", uploadUrl = "/admin/upload" }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => { setLoadError(false); }, [value]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data?.url) { onChange(data.url); toast.success("Photo uploaded"); }
      else toast.error("Upload succeeded but no image URL was returned");
    } catch (e) { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const setUrl = (raw) => onChange(normalizeImageUrl(raw));

  return (
    <div data-testid={testid}>
      <label className="label-overline">{label}</label>
      <div className="mt-1 flex items-start gap-3">
        <div className={`${aspect} w-24 shrink-0 rounded-sm overflow-hidden border border-slate-200 bg-slate-50 grid place-items-center relative`}>
          {value && !loadError
            ? <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} onError={() => setLoadError(true)} />
            : <ImageIcon className="w-6 h-6 text-slate-300" />}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid={`${testid}-btn`} disabled={busy} onClick={() => fileRef.current.click()}
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
              <UploadCloud className="w-4 h-4" /> {busy ? "Uploading…" : value ? "Replace" : "Upload Photo"}
            </button>
            {value && (
              <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange("")}
                className="inline-flex items-center gap-1 border border-slate-300 text-slate-500 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:text-[#E63946]">
                <X className="w-4 h-4" /> Remove
              </button>
            )}
          </div>
          <div>
            <input data-testid={`${testid}-url`} value={value || ""} onChange={(e) => setUrl(e.target.value)} placeholder="Or paste an image URL (optional)"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <p className="text-[10px] text-slate-400 mt-1">Uploading is recommended. Dropbox share links are converted automatically.</p>
          </div>
          {loadError && value && (
            <div data-testid={`${testid}-error`} className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              This image couldn't be displayed. Check the link is a direct image URL, or upload the photo instead.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
